// /clients - the clients the viewer may see, one row per Client doc: the
// firm's for admin+, those of the matters in scope for everyone else (the
// server's own scope). No fixture fallback: zero clients is a real state
// (a sheets-only sync creates no Client roots), so empty and unreachable
// states say so.

import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { languageName } from "../../lib/intakeLanguages";
import { Skeleton, SkeletonGroup } from "../../components/chrome/Skeleton";
import { SepList } from "../../components/chrome/SepList";
import { initialsOf } from "../../api/auth";
import { useClients } from "../../hooks/useClients";
import { useCurrentUser } from "../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";
import { SEARCH_MIN_QUERY, SEARCH_DEBOUNCE_MS } from "../../hooks/useGlobalSearch";
import type { ClientListRow } from "../../api/clients";

function rowSub(c: ClientListRow): ReactNode[] {
  const parts: ReactNode[] = [];
  if (c.alienNumber) {
    parts.push(
      <span key="a" className="tnum-mono">
        {c.alienNumber}
      </span>,
    );
  }
  if (c.status && c.status !== "active") parts.push(c.status);
  if (c.preferredLanguage) parts.push(languageName(c.preferredLanguage));
  if (c.primaryPhone) parts.push(c.primaryPhone);
  if (c.email) parts.push(c.email);
  return parts;
}

export function ClientsIndex() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [q]);
  const search = debouncedQ.length >= SEARCH_MIN_QUERY ? debouncedQ : undefined;

  const { data, isPending, isError, isFetching, isPlaceholderData } = useClients(search);
  const isAdmin = isAdminRole(useCurrentUser()?.role);
  const clients = data?.clients ?? [];
  const capped = data ? data.total > clients.length : false;

  // isPending, not isLoading: offline the ask is parked without fetching,
  // and that is still no answer. Placeholder data counts as settled.
  if (isPending) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <SkeletonGroup label="Loading clients">
            <Skeleton width="30%" height={22} />
            <Skeleton width="100%" height={84} radius={10} />
            <Skeleton width="100%" height={84} radius={10} />
          </SkeletonGroup>
        </div>
      </main>
    );
  }

  return (
    <main className="cc-main">
      <div className="cc-body">
        <div className="cc-index-head">
          <span className="title">Clients</span>
          {data && (
            <span className="count">
              {data.total} {search ? "matching" : ""} clients
            </span>
          )}
          <span className="blurb">
            One row per client. Each profile shows the client's matters you can see, with their
            judges, opposing counsel and relatives.
            {capped ? ` Showing ${clients.length} of ${data?.total}.` : ""}
          </span>
        </div>

        <input
          className="cc-field"
          aria-label="Search clients"
          placeholder="Search by name, phone, email, A-number..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 420, marginBottom: 4 }}
        />
        <div style={{ fontSize: 11, color: "var(--muted)", minHeight: 16, marginBottom: 8 }}>
          {q.trim().length > 0 && q.trim().length < SEARCH_MIN_QUERY
            ? `Type at least ${SEARCH_MIN_QUERY} characters to search.`
            : isFetching && data
              ? "Searching..."
              : ""}
        </div>

        {isError && (
          <div
            className="cc-card"
            style={{ padding: "10px 18px", marginBottom: 12, color: "var(--warn-600)" }}
          >
            Could not reach the server - the client list is unavailable.
          </div>
        )}

        {!isError && !isPlaceholderData && clients.length === 0 && (
          <div className="cc-card" style={{ padding: "10px 18px", color: "var(--muted)" }}>
            {search
              ? `No clients match "${debouncedQ}".`
              : isAdmin
                ? "No clients in the firm database yet."
                : "No clients on the matters assigned to you."}
          </div>
        )}

        {clients.length > 0 && (
          <div className="cc-card">
            {clients.map((c) => {
              const name = `${c.firstName} ${c.lastName}`;
              const sub = rowSub(c);
              return (
                <Link key={c._id} to={`/client/${c._id}`} className="cc-index-row cc-row-lift">
                  <span
                    aria-hidden
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: "var(--ink)",
                      color: "#fff",
                      display: "grid",
                      placeItems: "center",
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  >
                    {initialsOf(name)}
                  </span>
                  <div>
                    <div className="row-title">{name}</div>
                    {sub.length > 0 && (
                      <div className="row-sub">
                        <SepList parts={sub} />
                      </div>
                    )}
                  </div>
                  <div className="row-meta">
                    {c.mattersCount} {c.mattersCount === 1 ? "matter" : "matters"}
                    <div className="row-meta-sub">
                      {c.createdAt ? "created " + c.createdAt.slice(0, 10) : ""}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {data && (
          <div className="cc-detail-footer">
            <span>{search ? `${data.total} clients match` : `${data.total} clients`}</span>
          </div>
        )}
      </div>
    </main>
  );
}
